import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid, Switch, TextField, Typography } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { Controller, useForm } from 'react-hook-form';
import { post, put, get } from '../../axios/apiService';
import toast from 'react-hot-toast';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import Cron from 'react-cron-generator';
import { AsyncPaginate } from "react-select-async-paginate";
import AsyncSelect from "react-select/async";
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

function AddEditCampaignForm({ title = "Add / Edit Campaign" }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [cronValue, setCronValue] = useState("");
    const [isScheduleLater, setIsScheduleLater] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [value2, setValue2] = useState([]);
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Campaign);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            Name: ""
        },
        mode: "onChange"
    });

    const pageSize = 10;

    // -------------------------------
    // LOAD SUBSCRIBERS ASYNC
    // -------------------------------
    const loadOptions = async (search, loadedOptions, { page }) => {
        const filter = search
            ? `Email~LIKE~${search} OR FirstName~LIKE~${search} OR LastName~LIKE~${search}`
            : "";

        const url = `EmailSubscribeSync?$skip=${page * pageSize}&$top=${pageSize}${filter ? `&$filter=${filter}` : ""}`;

        const res = await get(url, null);
        const items = res?.values ?? [];
        const total = res?.totalCount ?? 0;

        return {
            options: items.map(item => ({
                value: item.id,
                label: item.email
            })),
            hasMore: (page + 1) * pageSize < total,
            additional: { page: page + 1 }
        };
    };

    // -------------------------------
    // LOAD TEMPLATE ASYNC
    // -------------------------------
    const loadEmailTemplateOptions = async (inputValue) => {
        const filter = inputValue ? `Name~LIKE~${inputValue}` : "";
        const url = `EmailTemplate?$skip=0&$top=20${filter ? `&$filter=${filter}` : ""}`;

        const res = await get(url, null);
        const items = res.values ?? [];

        return items.map(item => ({
            value: item.id,
            label: item.name
        }));
    };

    // -------------------------------
    // LOAD CAMPAIGN (EDIT MODE)
    // -------------------------------
    const loadCampaign = async () => {
        if (!id) return;

        setIsLoading(true);
        try {
            const res = await get(`Campaign/${id}`);
            if (!res) return;

            // Fill name
            setValue("Name", res.name);
            setValue("Subject", res.subject);


            // Subscribers → convert to select format
            const mappedSubscribers = (res.subscribers || []).map(sub => ({
                value: sub.id,
                label: sub.email
            }));
            setValue2(mappedSubscribers);

            // Template
            if (res.templateId && res.templateName) {
                setSelectedTemplate({
                    value: res.templateId,
                    label: res.templateName
                });
            }

            // Schedule toggle + cron
            setIsScheduleLater(res.isScheduleLater || false);
            if (res.cronExpression) {
                setCronValue(res.cronExpression);
            }
        } catch (err) {
            toast.error("Failed to load campaign");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCampaign();
    }, [id]);

    // -------------------------------
    // SUBMIT FORM
    // -------------------------------
    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            const payload = {
                Name: data.Name,
                Subject: data.Subject,
                Subscribers: value2.map(x => ({
                    id: x.value,
                    email: x.label
                })),
                TemplateId: selectedTemplate?.value || null,
                IsScheduleLater: isScheduleLater,
                CronExpression: isScheduleLater ? cronValue : null
            };

            if (!payload.TemplateId) {
                toast.error("Please select a template");
                setIsLoading(false);
                return;
            }

            if (isScheduleLater && !cronValue) {
                toast.error("Please choose a schedule time");
                setIsLoading(false);
                return;
            }

            if (id) {
                await put(`Campaign/${id}`, payload);
                toast.success("Campaign updated successfully");
            } else {
                await post("Campaign", payload);
                toast.success("Campaign created successfully");
            }

            navigate("/marketing/campaign");
        } catch (err) {
            toast.error("Error saving campaign");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <MainCard title={title} backButton={{ onClick: () => navigate('/marketing/campaign') }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>

                        {/* Name */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Campaign Name
                            </Typography>
                            <Controller
                                name="Name"
                                control={control}
                                rules={{ required: "Campaign name is required" }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Campaign Name *"
                                        error={!!errors.Name}
                                        helperText={errors.Name?.message}
                                        disabled={!canEdit}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Subject */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Subject
                            </Typography>
                            <Controller
                                name="Subject"
                                control={control}
                                defaultValue=""
                                rules={{ required: "Subject is required" }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Subject *"
                                        variant="outlined"
                                        error={!!errors.Subject}
                                        helperText={errors.Subject?.message}
                                    />

                                )}
                            />
                        </Grid>

                        {/* Subscribers */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Add Subscribers
                            </Typography>
                            <AsyncPaginate
                                isMulti
                                value={value2}
                                loadOptions={loadOptions}
                                onChange={setValue2}
                                additional={{ page: 0 }}
                                placeholder="Select subscribers..."
                                isDisabled={!canEdit}
                            />
                        </Grid>

                        {/* Template */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Select Template
                            </Typography>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadEmailTemplateOptions}
                                value={selectedTemplate}
                                onChange={setSelectedTemplate}
                                placeholder="Select Template..."
                                isSearchable
                                isDisabled={!canEdit}
                            />
                        </Grid>

                        {/* Toggle */}
                        <Grid item xs={12}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span>Publish Now</span>

                                <Switch
                                    checked={isScheduleLater}
                                    onChange={(e) => setIsScheduleLater(e.target.checked)}
                                    color="primary"
                                    disabled={!canEdit}
                                />

                                <span>Schedule Later</span>
                            </div>
                        </Grid>

                        {/* Cron */}
                        {isScheduleLater && (
                            <Grid item xs={12}>
                                <Cron
                                    onChange={(e) => setCronValue(e)}
                                    value={cronValue}
                                    showResultText={true}
                                    showResultCron={false}
                                    options={{
                                        headers: ['MINUTES', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY']
                                    }}
                                    disabled={!canEdit}
                                />
                            </Grid>
                        )}

                        {/* Submit */}
                        {
                            canEdit && (
                                <Grid item xs={12}>
                                    <Button type="submit" variant="contained" sx={{ mt: 2, px: 4 }} disabled={!canEdit}>
                                        {id ? "Update Campaign" : "Create Campaign"}
                                    </Button>
                                </Grid>
                            )
                        }
                    </Grid>
                </form>
            </MainCard>

            <LoadingBackdrop open={isLoading} />
        </div>
    );
}

export default AddEditCampaignForm;
